import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, CompactFilterBar, Input, Badge, Modal, Select, LoadingSpinner, Pagination, SearchableSelect } from '@/components/common';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import api, { getErrorMessage } from '@/services/api';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/store';
import { canManageVehicleMaster } from './masterDataAccess';
import { BulkActionBar, BulkSelectAllCheckbox, BulkSelectCheckbox, useBulkSelection } from '@/components/bulk';
import type { PaginatedResponse } from '@/types';

interface BodyType {
  id: string;
  name: string;
  code: string;
  // `vehicle_category` is the model field and the only key the serializer
  // reads or returns (VehicleBodyTypeSerializer). This page called it
  // `category`, so the column rendered blank, the edit form always fell
  // through to the '4w' default below, and the write payload carried a key
  // DRF drops -- the category could not be set from this screen at all.
  vehicle_category: string;
  is_active: boolean;
}

const categoryOptions = [
  { value: '2w', label: '2 Wheeler' },
  { value: '4w', label: '4 Wheeler' },
  { value: 'cv', label: 'Commercial Vehicle' },
];

// The list filter's "All Categories" option, kept separate from the form's
// categoryOptions above: the create/edit modal has no "unset" state (it
// always defaults to '4w'), but a filter needs one to mean "don't filter".
const categoryFilterOptions = [{ value: '', label: 'All Categories' }, ...categoryOptions];

// Status is a plain is_active boolean -- '' means "don't filter", so the
// value that goes on the wire is only ever 'true' or 'false' once chosen.
const statusOptions = [
  { value: '', label: 'All Statuses' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

const initialForm = { name: '', code: '', vehicle_category: '4w' };

const itemsPerPage = 20;

export function BodyTypesPage() {
  const queryClient = useQueryClient();
  // Read-only for everyone but the country admin -- which is exactly
  // where the server draws the line. See ./masterDataAccess.ts.
  const canManage = canManageVehicleMaster(useAuthStore().user?.role);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `search` drives the Input synchronously, `debouncedSearch` (300ms) is
  // what actually feeds the query below.
  const { term: search, debouncedTerm: debouncedSearch, setTerm: setSearch } = useDebouncedSearch();
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [formData, setFormData] = useState(initialForm);

  const { data, isLoading, isError, error, refetch } = useQuery<PaginatedResponse<BodyType>>({
    queryKey: ['body-types', page, debouncedSearch, filterCategory, filterStatus],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), page_size: String(itemsPerPage) });
      if (debouncedSearch) params.set('search', debouncedSearch);
      if (filterCategory) params.set('vehicle_category', filterCategory);
      if (filterStatus) params.set('is_active', filterStatus);
      const res = await api.get(`/vehicles/body-types/?${params.toString()}`);
      return res.data;
    },
  });
  const bodyTypes = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  const visibleIds = bodyTypes.map((b) => b.id);
  const selection = useBulkSelection(visibleIds);
  const activeFilterCount = [filterCategory, filterStatus].filter(Boolean).length;

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['body-types'] });
  };

  const createMutation = useMutation({
    mutationFn: async (data: typeof initialForm) => {
      if (editingId) {
        const res = await api.patch(`/vehicles/body-types/${editingId}/`, data);
        return res.data;
      }
      const res = await api.post('/vehicles/body-types/', data);
      return res.data;
    },
    onSuccess: () => {
      invalidate();
      toast.success(editingId ? 'Body type updated' : 'Body type created');
      setShowModal(false);
      setEditingId(null);
      setFormData(initialForm);
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api.delete(`/vehicles/body-types/${id}/`);
    },
    onSuccess: () => {
      invalidate();
      toast.success('Body type deleted');
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  });

  const handleEdit = (bt: BodyType) => {
    setEditingId(bt.id);
    setFormData({ name: bt.name, code: bt.code, vehicle_category: bt.vehicle_category || '4w' });
    setShowModal(true);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Delete this body type? This cannot be undone.')) {
      deleteMutation.mutate(id);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData(initialForm);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Body Types</h1>
          <p className="text-gray-500 dark:text-gray-400 mt-1">Manage vehicle body types</p>
        </div>
        {canManage && (
          <Button variant="primary" leftIcon={<PlusIcon className="w-4 h-4" />} onClick={() => setShowModal(true)}>
            Add Body Type
          </Button>
        )}
      </div>

      <CompactFilterBar
        search={search}
        onSearchChange={(value) => {
          setSearch(value);
          setPage(1);
        }}
        searchPlaceholder="Search body types..."
        activeFilterCount={activeFilterCount}
        onClearFilters={() => {
          setFilterCategory('');
          setFilterStatus('');
          setPage(1);
        }}
      >
        <Select
          id="body-type-filter-category"
          label="Category"
          options={categoryFilterOptions}
          value={filterCategory}
          onChange={(e) => {
            setFilterCategory(e.target.value);
            setPage(1);
          }}
        />
        <Select
          id="body-type-filter-status"
          label="Status"
          options={statusOptions}
          value={filterStatus}
          onChange={(e) => {
            setFilterStatus(e.target.value);
            setPage(1);
          }}
        />
      </CompactFilterBar>

      {canManage && (
        <BulkActionBar
          resource="vehicles/body-types"
          entityName="body types"
          selectedIds={selection.selectedIds}
          onChanged={invalidate}
          onClear={selection.clear}
        />
      )}

      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <LoadingSpinner />
          </div>
        ) : isError ? (
          // A failed request is not "there are no body types" -- those are
          // two different states and this platform has a habit of confusing
          // them.
          <div role="alert" className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900">Could not load body types</h3>
            <p className="mt-2 text-gray-500">{getErrorMessage(error)}</p>
            <Button className="mt-4" variant="outline" size="sm" onClick={() => refetch()}>
              Try again
            </Button>
          </div>
        ) : bodyTypes.length === 0 ? (
          <div className="text-center py-12">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">No body types found</h3>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Get started by adding a body type.</p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700/50">
                  <tr>
                    {canManage && (
                      <th className="px-6 py-3 text-left">
                        <BulkSelectAllCheckbox
                          checked={selection.allVisibleSelected}
                          indeterminate={selection.someVisibleSelected}
                          onChange={selection.toggleAllVisible}
                        />
                      </th>
                    )}
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Code</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Category</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {bodyTypes.map((bt) => (
                    <tr key={bt.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      {canManage && (
                        <td className="px-6 py-4">
                          <BulkSelectCheckbox
                            checked={selection.isSelected(bt.id)}
                            onChange={() => selection.toggle(bt.id)}
                            label={`Select ${bt.name}`}
                          />
                        </td>
                      )}
                      <td className="px-6 py-4 text-sm font-medium text-gray-900 dark:text-white">{bt.name}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">{bt.code}</td>
                      <td className="px-6 py-4 text-sm text-gray-700 dark:text-gray-300">
                        {categoryOptions.find((o) => o.value === bt.vehicle_category)?.label || bt.vehicle_category}
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant={bt.is_active ? 'success' : 'warning'}>
                          {bt.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1">
                          {canManage && (
                          <>
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(bt)}>
                            <PencilIcon className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDelete(bt.id)}>
                            <TrashIcon className="w-4 h-4 text-red-600" />
                          </Button>
                          </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>

      <Modal isOpen={showModal} onClose={handleCloseModal} title={editingId ? 'Edit Body Type' : 'Add Body Type'}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate(formData);
          }}
          className="space-y-4"
        >
          <Input
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />
          <Input
            label="Code"
            value={formData.code}
            onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
            required
          />
          <SearchableSelect
            label="Category"
            required
            value={formData.vehicle_category}
            onChange={(value) => setFormData({ ...formData, vehicle_category: value })}
            options={categoryOptions}
          />
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={handleCloseModal}>Cancel</Button>
            <Button type="submit" isLoading={createMutation.isPending}>{editingId ? 'Update' : 'Create'}</Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default BodyTypesPage;
